import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LucideAngularModule,
  Eye,
  EyeOff,
  RefreshCw,
  MoreVertical,
  Edit,
  Trash2,
  AlertCircle,
  Monitor,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Plus,
} from 'lucide-angular';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PosService } from '../../core/services/pos.service';
import { PosTerminal, CreatePosRequest, UpdatePosRequest } from '../../core/models/pos.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-pos',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, FormsModule, ConfirmDialogModule, TranslatePipe],
  providers: [ConfirmationService],
  templateUrl: './pos.component.html',
})
export class PosComponent implements OnInit {
  private readonly posService = inject(PosService);
  private readonly confirm = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef$ = inject(DestroyRef);

  Eye = Eye;
  EyeOff = EyeOff;
  RefreshCw = RefreshCw;
  MoreVertical = MoreVertical;
  Edit = Edit;
  Trash2 = Trash2;
  AlertCircle = AlertCircle;
  Monitor = Monitor;
  ArrowLeft = ArrowLeft;
  CheckCircle = CheckCircle;
  XCircle = XCircle;
  Plus = Plus;

  storeId = signal('');
  posList = signal<PosTerminal[]>([]);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  visibleCodeIds = signal<Set<string>>(new Set());
  regeneratingIds = signal<Set<string>>(new Set());

  // Create dialog
  isCreateOpen = signal(false);
  isCreateLoading = signal(false);
  createError = signal<string | null>(null);
  createForm = { name: '' };

  // Edit dialog
  isEditOpen = signal(false);
  isEditLoading = signal(false);
  editError = signal<string | null>(null);
  editForm = { id: '', name: '', isActive: true };

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('storeId') ?? '';
    this.storeId.set(id);
    if (id) this.loadPos();
  }

  loadPos() {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.posService
      .getPosByStore(this.storeId())
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          if (res.success) this.posList.set(res.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.errorMessage.set(this.translate.instant('POS_TERMINALS.LOAD_ERROR'));
          this.isLoading.set(false);
        },
      });
  }

  isCodeVisible(id: string): boolean {
    return this.visibleCodeIds().has(id);
  }

  toggleCodeVisibility(id: string) {
    this.visibleCodeIds.update((s) => {
      const next = new Set(s);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  isRegenerating(id: string): boolean {
    return this.regeneratingIds().has(id);
  }

  /**
   * Yangi kod yaratish — QAYTARIB BO'LMAYDIGAN amal, shuning uchun tasdiq so'raladi.
   *
   * Backenddagi `RegeneratePosCodeCommand` kassani `IsActive = false` qiladi, POS
   * autentifikatsiyasi esa (`PosAuthMiddleware`) aynan `IsActive` ni tekshiradi.
   * Ya'ni ishlab turgan kassa shu zahoti serverdan uziladi: uning so'rovlari
   * `POS_UNAUTHORIZED` bilan rad etiladi va ilovani yangi kod bilan qaytadan
   * faollashtirmaguncha ishlamaydi. Faollashtirilmagan kassada esa yo'qotadigan
   * narsa yo'q — ogohlantirish shunga qarab yumshatiladi.
   */
  regenerateCode(pos: PosTerminal) {
    this.confirm.confirm({
      header: this.translate.instant('POS_TERMINALS.REGENERATE.TITLE'),
      icon: 'pi pi-exclamation-triangle',
      message: this.translate.instant(
        pos.isActive
          ? 'POS_TERMINALS.REGENERATE.CONFIRM_ACTIVE'
          : 'POS_TERMINALS.REGENERATE.CONFIRM_INACTIVE',
        { name: pos.name },
      ),
      acceptLabel: this.translate.instant('POS_TERMINALS.REGENERATE.ACCEPT'),
      rejectLabel: this.translate.instant('POS_TERMINALS.REGENERATE.REJECT'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.doRegenerateCode(pos),
    });
  }

  private doRegenerateCode(pos: PosTerminal) {
    this.regeneratingIds.update((s) => new Set(s).add(pos.id));
    this.posService
      .regenerateCode(pos.id)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          if (res.success) {
            // Faqat kodni almashtirish yetmaydi: server kassani nofaol qiladi va
            // yangi muddat qo'yadi, ya'ni yonidagi "faol" belgisi bilan muddat
            // eskirib qolardi. Shuning uchun ro'yxat serverdan qayta o'qiladi.
            this.loadPos();
            this.visibleCodeIds.update((s) => new Set(s).add(pos.id));
          }
          this.regeneratingIds.update((s) => {
            const next = new Set(s);
            next.delete(pos.id);
            return next;
          });
        },
        error: () => {
          this.regeneratingIds.update((s) => {
            const next = new Set(s);
            next.delete(pos.id);
            return next;
          });
        },
      });
  }

  openCreate() {
    this.createForm = { name: '' };
    this.createError.set(null);
    this.isCreateOpen.set(true);
  }

  closeCreate() {
    this.isCreateOpen.set(false);
    this.createError.set(null);
  }

  handleCreate() {
    if (!this.createForm.name.trim()) return;
    this.isCreateLoading.set(true);
    const req: CreatePosRequest = { storeId: this.storeId(), name: this.createForm.name };
    this.posService
      .createPos(req)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () => {
          this.loadPos();
          this.closeCreate();
          this.isCreateLoading.set(false);
        },
        error: (err) => {
          this.createError.set(
            err.error?.message || this.translate.instant('POS_TERMINALS.CREATE_ERROR'),
          );
          this.isCreateLoading.set(false);
        },
      });
  }

  openEdit(pos: PosTerminal) {
    this.editForm = { id: pos.id, name: pos.name, isActive: pos.isActive };
    this.editError.set(null);
    this.isEditOpen.set(true);
  }

  closeEdit() {
    this.isEditOpen.set(false);
    this.editError.set(null);
  }

  handleEdit() {
    if (!this.editForm.name.trim()) return;
    this.isEditLoading.set(true);
    const req: UpdatePosRequest = {
      id: this.editForm.id,
      name: this.editForm.name,
      isActive: this.editForm.isActive,
    };
    this.posService
      .updatePos(this.editForm.id, req)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () => {
          this.loadPos();
          this.closeEdit();
          this.isEditLoading.set(false);
        },
        error: (err) => {
          this.editError.set(
            err.error?.message || this.translate.instant('POS_TERMINALS.EDIT_ERROR'),
          );
          this.isEditLoading.set(false);
        },
      });
  }

  handleDelete(id: string) {
    if (confirm(this.translate.instant('POS_TERMINALS.DELETE_CONFIRM'))) {
      this.posService
        .deletePos(id)
        .pipe(takeUntilDestroyed(this.destroyRef$))
        .subscribe({
          next: () => this.loadPos(),
          error: (err) =>
            alert(err.error?.message || this.translate.instant('POS_TERMINALS.DELETE_ERROR')),
        });
    }
  }

  goBack() {
    this.router.navigate(['/app/stores']);
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('uz-UZ', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }
}
